import type { BidPseudoStatus } from '@/helpers/getBidPseudoStatus'
import { getBidPseudoStatus } from '@/helpers/getBidPseudoStatus'
import type {
  BidsSearchResponse,
  BidsSearchResult,
} from '@/queries/uniqueUserBids.types'

/** Display cap; the query fetches more (see `useDashboardBids`). */
export const MAX_BID_ROWS = 5

export type BidPositionTag = 'WINNING' | 'OUTBID'

export type DashboardBidRow = {
  bidId: string
  listingId: string
  auctionId: string
  title: string | null
  /** Current high bid on the auction, not the buyer's own bid. */
  currentBidAmount: number
  maxBidAmount: number
  bidCanceled: boolean
  /** Server-computed next valid bid amount — the rebid stepper's floor. */
  nextMinBidAmount: number | null
  /** Bid amount granularity; also the rebid stepper's step size. */
  bidPrecision: number
  startPrice: number | null
  currency: string
  positionTag: BidPositionTag
  endTime: string
}

type DashboardBidRows = {
  rows: DashboardBidRow[]
  /** True open-bid total from the query, not the capped row count. */
  total: number
}

// Exhaustive so the panel can never render an untagged row; closed/canceled
// statuses collapse onto the side the bid ended on. NO_BID is filtered in
// deriveBidRows before reaching this map; the entry satisfies exhaustiveness only.
const POSITION_TAG_BY_PSEUDO_STATUS: Record<BidPseudoStatus, BidPositionTag> = {
  NO_BID: 'OUTBID',
  WINNING: 'WINNING',
  WON: 'WINNING',
  WON_CANCEL: 'WINNING',
  WON_CREATED: 'WINNING',
  WON_ORDER_CANCEL: 'WINNING',
  LOSING: 'OUTBID',
  LOST: 'OUTBID',
  LOST_CANCEL: 'OUTBID',
}

const toDashboardBidRow = (bid: BidsSearchResult): DashboardBidRow => {
  const { auction, bidAmount, listing } = bid

  return {
    bidId: bid.bidId,
    listingId: auction.listingId,
    auctionId: bid.auctionId,
    title: auction.attributes?.title ?? listing?.lot.title ?? null,
    currentBidAmount: auction.winningBidAmount,
    maxBidAmount: bidAmount,
    bidCanceled: bid.canceled,
    nextMinBidAmount: auction.nextMinBidAmount ?? null,
    // fe-core treats missing precision as 1, and 0 is unusable — so `||`.
    bidPrecision: auction.bidPrecision || 1,
    startPrice: auction.startPrice ?? null,
    // `currency` isn't on the listing's exported type yet; USD fallback for
    // legacy pre-multi-currency listings.
    currency: (listing as { currency?: string } | null)?.currency ?? 'USD',
    positionTag: POSITION_TAG_BY_PSEUDO_STATUS[getBidPseudoStatus(bid)],
    endTime: auction.actualEndTime || auction.initialEndTime,
  }
}

const POSITION_TAG_SORT_RANK: Record<BidPositionTag, number> = {
  OUTBID: 0,
  WINNING: 1,
}

const compareDashboardBidRows = (
  a: DashboardBidRow,
  b: DashboardBidRow
): number => {
  const tagRank =
    POSITION_TAG_SORT_RANK[a.positionTag] -
    POSITION_TAG_SORT_RANK[b.positionTag]
  if (tagRank !== 0) return tagRank

  return Date.parse(a.endTime) - Date.parse(b.endTime)
}

/** Outbid rows first, soonest-ending first within each group, capped. */
export const deriveBidRows = (
  data: Pick<BidsSearchResponse, 'bids' | 'total'>
): DashboardBidRows => ({
  rows: data.bids
    .filter((bid) => getBidPseudoStatus(bid) !== 'NO_BID')
    .map(toDashboardBidRow)
    .sort(compareDashboardBidRows)
    .slice(0, MAX_BID_ROWS),
  total: data.total,
})

export const URGENT_COUNTDOWN_THRESHOLD_MS = 60 * 60 * 1000

export type BidCountdownStatus = 'active' | 'urgent' | 'ended'

type BidCountdownState =
  | { status: 'ended' }
  | {
      status: 'active' | 'urgent'
      /** Milliseconds until the status next changes, so the live hook knows when to re-derive. */
      msUntilStatusChange: number
    }

/**
 * Status only — the ticking countdown text is fe-core's `Countdown`. Strictly
 * under one hour is urgent; at exactly one hour the row is still `active`.
 */
export const getBidCountdownState = (
  endTime: string,
  now: Date
): BidCountdownState => {
  const remainingMs = Date.parse(endTime) - now.getTime()
  if (Number.isNaN(remainingMs) || remainingMs <= 0) return { status: 'ended' }

  if (remainingMs < URGENT_COUNTDOWN_THRESHOLD_MS) {
    return { status: 'urgent', msUntilStatusChange: remainingMs }
  }

  return {
    status: 'active',
    msUntilStatusChange: remainingMs - URGENT_COUNTDOWN_THRESHOLD_MS,
  }
}
